import * as React from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@core/lib/utils";

/**
 * Standalone accordion components - no Radix root context required.
 * Each AccordionItem manages its own open/closed state, so items can be
 * used independently (e.g. inside a flat sidebar) without needing a parent
 * <Accordion> wrapper. API stays compatible with the previous Radix-based
 * implementation: AccordionItem (with optional `value`), AccordionTrigger,
 * AccordionContent.
 */

interface AccordionItemContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const AccordionItemContext = React.createContext<AccordionItemContextValue | null>(null);

interface AccordionProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: "single" | "multiple";
  collapsible?: boolean;
  defaultValue?: string | string[];
  value?: string | string[];
  onValueChange?: (value: any) => void;
}

const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  ({ className, children, type, collapsible, defaultValue, value, onValueChange, ...props }, ref) => (
    <div ref={ref} className={cn(className)} {...props}>
      {children}
    </div>
  )
);
Accordion.displayName = "Accordion";

interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: string;
  defaultOpen?: boolean;
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ className, defaultOpen = false, value, children, ...props }, ref) => {
    const [open, setOpen] = React.useState(defaultOpen);
    return (
      <AccordionItemContext.Provider value={{ open, setOpen }}>
        <div
          ref={ref}
          data-state={open ? "open" : "closed"}
          className={cn("border-b dark:bg-surface-950 dark:border-surface-800", className)}
          {...props}
        >
          {children}
        </div>
      </AccordionItemContext.Provider>
    );
  }
);
AccordionItem.displayName = "AccordionItem";

interface AccordionTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  hideChevron?: boolean;
}

const AccordionTrigger = React.forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ className, children, hideChevron, onClick, ...props }, ref) => {
    const ctx = React.useContext(AccordionItemContext);
    const open = ctx?.open ?? false;
    return (
      <h3 className="flex">
        <button
          type="button"
          ref={ref}
          data-state={open ? "open" : "closed"}
          aria-expanded={open}
          onClick={(e) => {
            ctx?.setOpen(!open);
            onClick?.(e);
          }}
          className={cn(
            "bg-surface-50 dark:bg-surface-950 dark:text-white flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
            className
          )}
          {...props}
        >
          {children}
          {!hideChevron && <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />}
        </button>
      </h3>
    );
  }
);
AccordionTrigger.displayName = "AccordionTrigger";

interface AccordionContentProps extends React.HTMLAttributes<HTMLDivElement> {}

const AccordionContent = React.forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ className, children, ...props }, ref) => {
    const ctx = React.useContext(AccordionItemContext);
    const open = ctx?.open ?? true;
    if (!open) return null;
    return (
      <div
        ref={ref}
        data-state={open ? "open" : "closed"}
        className="overflow-hidden text-sm dark:text-white"
        {...props}
      >
        <div className={cn("pb-4 pt-0", className)}>{children}</div>
      </div>
    );
  }
);
AccordionContent.displayName = "AccordionContent";

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
